// Yoga Warminster - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaWarminster = () => {
  usePageMeta({
    title: "Yoga Classes Warminster | Annelie Lily",
    description: "Yoga classes for Warminster, Wiltshire. Small group Hatha & Yin yoga in Frome, 20 minutes from Warminster. Personal attention, beginners welcome.",
    canonical: "https://annelielily.com/areas/yoga-warminster"
  });

  const locationSchema = generateLocationSchema({
    townName: "Warminster",
    description: "Yoga classes for Warminster at Annelie Lily Yoga in Frome. Small group classes and private sessions 20 minutes from Warminster.",
    url: "https://annelielily.com/yoga-warminster"
  });

  const faqs = [
    {
      question: "How do I get to Frome from Warminster?",
      answer: "Head south on the A350, then pick up the A362 or A361 into Frome. It's about 20 minutes through lovely Wiltshire and Somerset countryside."
    },
    {
      question: "What times are classes available?",
      answer: "I run classes at various times throughout the week, including evening sessions that work well for those coming from Warminster after work. Get in touch for the current schedule."
    },
    {
      question: "I have a back problem — can I still do yoga?",
      answer: "Often, yes. Many people with back issues find yoga helpful when taught carefully. Let me know what's going on before class, and I can offer appropriate modifications. For complex issues, a private session might be the best starting point."
    },
    {
      question: "Is parking available?",
      answer: "Yes, there's parking available nearby. I'll send you full directions when you book your first class."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Warminster" currentSlug="areas/yoga-warminster" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Warminster</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Warminster</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              A 20-minute drive through the countryside brings you to Frome, where yoga classes 
              are small, personal, and genuinely focused on you as an individual.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />20 min from Warminster</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Warminster Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Warminster sits just across the Wiltshire border from Frome — close enough that 
              my studio is genuinely accessible for regular practice. Many of my students make 
              the journey weekly, finding that the drive itself becomes part of the wind-down.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              kept deliberately small, usually 6-8 people. This means I can actually see what's 
              happening in your practice and offer adjustments that make a difference.
            </p>
            <p>
              If you're <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">new to yoga</Link>, 
              there's no need to worry. I teach in a way that accommodates all levels, with easier 
              options for every pose. You work where your body is today, not where you think it should be.
            </p>
            <p>
              The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Warminster</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga", desc: "Active practice building strength and flexibility. Includes postures, breathing, and deep relaxation.", link: "/areas/yoga-warminster/hatha-yoga" },
              { title: "Yin Yoga", desc: "Slow, meditative practice with long-held poses. Particularly good for stress and tight muscles.", link: "/areas/yoga-warminster/yin-yoga" },
              { title: "Private Sessions", desc: "One-to-one yoga tailored to your specific needs and goals.", link: "/areas/yoga-warminster/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Questions from Warminster Students
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaWarminster;
